import { Bell, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RoleIndicator } from "@/components/RoleIndicator";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WorkspaceSwitcher } from "@/components/layout/WorkspaceSwitcher";
import { useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBranding } from "@/hooks/useBranding";

export const Header = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const { branding } = useBranding();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
      navigate("/auth/login");
    } catch (error) {
      toast.error("Failed to sign out", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    }
  };

  // Sticky header with hardware acceleration and backdrop blur for smooth scrolling
  // bg-card/95 provides fallback for browsers without backdrop-filter support
  return (
    <header className="border-b bg-card/95 backdrop-blur-sm backdrop-saturate-150 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0 z-40">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="shrink-0 min-h-[44px] min-w-[44px]"
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt="Logo" 
              className="h-8 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <h1 className="text-base sm:text-lg font-semibold truncate">
              Dashboard
            </h1>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-3">
          <div className="hidden sm:block">
            <RoleIndicator />
          </div>
          <WorkspaceSwitcher />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notifications"
            className="min-h-[44px] min-w-[44px]"
          >
            <Bell className="h-5 w-5" />
          </Button>
          {isMobile ? (
            <Button
              variant="outline"
              size="icon"
              onClick={handleSignOut}
              className="min-h-[44px] min-w-[44px]"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="min-h-[44px]"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
